/**
 * Filter state and option lists for the All Deployments page. Non-component
 * module so the page and the filter bar can share it and tests can import it.
 */
import { isActiveStatus } from '@/utils/format'
import type { DeploymentFilters, DeploymentRequest, DeploymentStatus, Environment } from '@/types'
import type { ProvisioningStatus, ServerMode } from '@/types/workwise'

export type FilterChip = 'all' | 'active' | 'completed' | 'failed'

export interface FilterState {
  search: string
  customer: string
  /** Product slug. */
  product: string
  environment: Environment | ''
  status: DeploymentStatus | ''
  dbMode: ServerMode | ''
  gatewayMode: ServerMode | ''
  provisioning: ProvisioningStatus | ''
}

export const EMPTY_FILTERS: FilterState = {
  search: '',
  customer: '',
  product: '',
  environment: '',
  status: '',
  dbMode: '',
  gatewayMode: '',
  provisioning: '',
}

export const ENVIRONMENTS: Array<{ value: Environment | ''; label: string }> = [
  { value: '', label: 'All Environments' },
  { value: 'dev', label: 'Development' },
  { value: 'stg', label: 'Staging' },
  { value: 'qa', label: 'QA' },
  { value: 'uat', label: 'UAT' },
  { value: 'demo', label: 'Demo' },
  { value: 'production', label: 'Production' },
  { value: 'uat_usgov', label: 'USGOV UAT' },
  { value: 'prod_usgov', label: 'USGOV Prod' },
]

export const STATUSES: Array<{ value: DeploymentStatus | ''; label: string }> = [
  { value: '', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'validating', label: 'Validating' },
  { value: 'provisioning_infra', label: 'Provisioning' },
  { value: 'planning_infra', label: 'Planning Infrastructure' },
  { value: 'awaiting_plan_approval', label: 'Awaiting Plan Approval' },
  { value: 'applying_infra', label: 'Applying Infrastructure' },
  { value: 'verifying_server', label: 'Verifying Server' },
  { value: 'infra_ready', label: 'Infrastructure Ready' },
  { value: 'deploying_app', label: 'Deploying' },
  { value: 'configuring', label: 'Configuring' },
  { value: 'running_health_checks', label: 'Health Checks' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'rolled_back', label: 'Rolled Back' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'destroying', label: 'Destroying' },
  { value: 'destroyed', label: 'Destroyed' },
]

export const PROVISIONING_OPTIONS: Array<{ value: ProvisioningStatus | ''; label: string }> = [
  { value: '', label: 'All Provisioning' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Provisioning', label: 'Provisioning' },
  { value: 'Deployed', label: 'Deployed' },
  { value: 'Failed', label: 'Failed' },
  { value: 'Destroying', label: 'Destroying' },
  { value: 'Destroyed', label: 'Destroyed' },
]

export const MODE_OPTIONS = (all: string): Array<{ value: ServerMode | ''; label: string }> => [
  { value: '', label: all },
  { value: 'shared', label: 'Shared' },
  { value: 'dedicated', label: 'Dedicated' },
]

export const LIST_PAGE_SIZE = 20
/** The By-customer view groups one page, so it asks for the largest page the API allows. */
export const GROUPED_PAGE_SIZE = 200

/** The status the chip implies; the "active" chip filters client-side instead. */
export function chipStatus(chip: FilterChip, status: DeploymentStatus | ''): DeploymentStatus | '' {
  if (chip === 'active') return ''
  if (chip === 'completed') return 'completed'
  if (chip === 'failed') return 'failed'
  return status
}

export interface QueryInput {
  filters: FilterState
  chip: FilterChip
  ordering: string
  page: number
  pageSize: number
}

/** Builds the API query: empty values are omitted, nothing else is changed. */
export function toQueryFilters({ filters, chip, ordering, page, pageSize }: QueryInput): DeploymentFilters {
  const status = chipStatus(chip, filters.status)
  return {
    search: filters.search || undefined,
    customer: filters.customer.trim() || undefined,
    product: filters.product || undefined,
    environment: filters.environment || undefined,
    status: status || undefined,
    db_mode: filters.dbMode || undefined,
    gateway_mode: filters.gatewayMode || undefined,
    provisioning_status: filters.provisioning || undefined,
    ordering: ordering || undefined,
    page,
    page_size: pageSize,
  }
}

/** The "Active" chip keeps only rows that are running or waiting to start. */
export function applyChip(rows: DeploymentRequest[], chip: FilterChip): DeploymentRequest[] {
  return chip === 'active' ? rows.filter((d) => isActiveStatus(d.status) || d.status === 'pending') : rows
}

export function hasActiveFilters(filters: FilterState, chip: FilterChip): boolean {
  return chipStatus(chip, filters.status) !== '' || Object.entries(filters).some(([key, value]) => key !== 'status' && value !== '')
}
